/**
 * Native Shoes — MSRP line item properties
 * ────────────────────────────────────────────────────────────────────────────
 * Author:   Anton Siedlecki
 * Version:  1.2.0
 * Date:     2026-10-21
 * Feature:  .projects/features/317-msrp-line-item
 *
 * WHAT
 * Every cart line carries two hidden properties so the API side knows what
 * MSRP was when an order was placed (reporting only):
 *   _msrp           the number, e.g. "120.00"
 *   _msrp_currency  "CAD" for the Canada market, "USD" everywhere else
 * The underscore keeps them out of the cart, checkout and the default emails.
 *
 * VALUE RULE
 * The variant's compare-at price when one is set, otherwise its regular price.
 * Canada: the Canada market's prices (CAD). The US: the US market's prices (USD).
 * Every other country: always the US catalog in USD, whatever that country's own
 * catalog says, so the US price is requested explicitly from the Storefront API
 * (@inContext(country: US)). This file only ever reads
 * Shopify's own data (the PDP island, /cart.js, /products/<handle>.js), never
 * page text, so the Anniversary Sale price preview can never leak in.
 *
 * PARTS
 *  1. PDP: when the variant id input changes (size selector, variant picker),
 *     update the two hidden inputs from the JSON island in
 *     snippets/native-msrp-fields.liquid.
 *  2. Cart refresh: re-write both properties on every line whose values are
 *     missing or out of date (a bag left for weeks, lines added by quick add or
 *     Swym that never had them). Runs on the cart page, after cart changes and
 *     right before checkout.
 *  3. Checkout guard: the click on a checkout button waits for the refresh
 *     (4 second cap, never blocks), then continues as a normal click.
 *
 * Debug: ?native_msrp_debug=1 logs to the console.
 *
 * CHANGELOG
 *  1.2.0 (2026-10-21) Fix: Global-e countries show local currency to Liquid (ALL for Albania) and can lose the
 *                     compare-at price, and the US override only ran once a size was picked, so a local
 *                     number was labelled USD. Now the PDP inputs are server-rendered empty and disabled for
 *                     those countries (never submitted) and filled from the US catalog on load; if the US
 *                     lookup fails the line gets no MSRP instead of a wrong one (PDP and cart refresh).
 *  1.1.0 (2026-10-21) Shoppers outside the US and Canada always get the US catalog price in USD,
 *                     read from the Storefront API with @inContext(country: US), so a country
 *                     catalog with its own prices can never change MSRP. Falls back to the
 *                     storefront's own price if that request fails.
 *  1.0.0 (2026-10-20) First version.
 */
(function () {
  'use strict';

  var NS = (window.NativeShops = window.NativeShops || {});
  if (NS.msrp) return;

  var DEBUG = /[?&]native_msrp_debug=1/.test(window.location.search);
  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[native-msrp]'].concat([].slice.call(arguments)));
  }

  // ── Shared rules ─────────────────────────────────────────────────────────
  function country() {
    var c = (NS.config && NS.config.countryCode) || (window.Shopify && window.Shopify.country) || '';
    return String(c).toUpperCase();
  }
  function currencyFor() {
    return country() === 'CA' ? 'CAD' : 'USD';
  }
  function fmt(cents) {
    var n = Math.round(Number(cents));
    if (!isFinite(n) || n < 0) return '';
    var rest = n % 100;
    return Math.floor(n / 100) + '.' + (rest < 10 ? '0' : '') + rest;
  }
  // Compare-at when set, otherwise the regular price (both integer cents).
  function msrpCents(price, compareAt) {
    return compareAt && Number(compareAt) > 0 ? Number(compareAt) : Number(price);
  }

  // Every country except the US and Canada must report the US catalog price,
  // so those shoppers get an explicit US lookup instead of the storefront's price.
  function needsUsLookup() {
    var c = country();
    return c !== 'CA' && c !== 'US';
  }
  var cfg = (function () {
    try { return JSON.parse(document.getElementById('native-msrp-config').textContent) || {}; } catch (e) { return {}; }
  })();

  // handle -> Promise({ variantId: { price, compare } } in cents), US catalog
  var usCache = {};
  function getUsPrices(handle) {
    if (!cfg.token || !cfg.domain) return Promise.reject(new Error('no storefront token'));
    if (!usCache[handle]) {
      var q = 'query($h:String!) @inContext(country: US) { product(handle:$h) { variants(first:250) { nodes { id price { amount } compareAtPrice { amount } } } } }';
      usCache[handle] = fetch('https://' + cfg.domain + '/api/2026-04/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': cfg.token },
        body: JSON.stringify({ query: q, variables: { h: handle } })
      }).then(function (r) { return r.json(); }).then(function (j) {
        var nodes = j && j.data && j.data.product && j.data.product.variants && j.data.product.variants.nodes;
        if (!nodes) throw new Error('no us prices');
        var map = {};
        nodes.forEach(function (n) {
          var id = String(n.id).split('/').pop();
          map[id] = {
            price: Math.round(parseFloat(n.price.amount) * 100),
            compare: n.compareAtPrice ? Math.round(parseFloat(n.compareAtPrice.amount) * 100) : 0
          };
        });
        return map;
      }).catch(function (err) { delete usCache[handle]; throw err; });
    }
    return usCache[handle];
  }

  // ── 1. PDP: keep the hidden inputs in step with the selected variant ─────
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('input[name="id"]') || !t.form) return;
    if (!t.form.querySelector('[data-native-msrp-input]')) return;
    var island = t.form.querySelector('script[data-native-msrp]');
    var data = null;
    try { data = island ? JSON.parse(island.textContent) : null; } catch (err) { data = null; }
    if (!data || !data.variants) return;
    applyPdp(t.form, data, String(t.value));
  });

  function applyPdp(form, data, variantId) {
    var valueInput = form.querySelector('[data-native-msrp-input]');
    if (!valueInput) return;
    var cents = null;
    if (data.lookup) {
      var us = data.handle && usOverride[data.handle];
      cents = us && us[variantId] != null ? us[variantId] : null;      // US catalog only, never the market price
    } else {
      cents = data.variants[variantId];
    }
    if (cents == null) return;                       // no selection or no US price yet: keep the last value
    valueInput.value = fmt(cents);
    valueInput.disabled = false;
    var curInput = form.querySelector('[data-native-msrp-currency-input]');
    if (curInput) {
      if (data.currency) curInput.value = data.currency;
      curInput.disabled = false;
    }
    log('pdp inputs', variantId, valueInput.value);
  }

  // US catalog price per variant for countries outside the US and Canada.
  var usOverride = {};
  function loadPdpUsPrices() {
    var forms = document.querySelectorAll('form');
    Array.prototype.forEach.call(forms, function (form) {
      var island = form.querySelector('script[data-native-msrp]');
      if (!island) return;
      var data = null;
      try { data = JSON.parse(island.textContent); } catch (err) { return; }
      if (!data || !data.lookup || !data.handle) return;
      getUsPrices(data.handle).then(function (map) {
        var out = {};
        Object.keys(map).forEach(function (id) { out[id] = msrpCents(map[id].price, map[id].compare); });
        usOverride[data.handle] = out;
        var idInput = form.querySelector('input[name="id"]');
        var id = idInput && idInput.value ? String(idInput.value) : String(data.initial || '');
        if (id) applyPdp(form, data, id);
      }, function (err) { log('us lookup failed (PDP), no MSRP on this form', err); });
    });
  }

  // ── 2. Cart refresh ──────────────────────────────────────────────────────
  var productCache = {};                              // handle -> Promise(product json), page life only

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
        .then(function (p) {
          if (!needsUsLookup()) return p;
          // Outside the US and Canada: swap in the US catalog prices (fall back to the market's own).
          return getUsPrices(handle).then(function (map) {
            (p.variants || []).forEach(function (v) {
              var u = map[String(v.id)];
              if (u) { v.price = u.price; v.compare_at_price = u.compare; }
            });
            return p;
          }, function (err) { log('us lookup failed (cart), skipping lines', err); return null; });
        })
        .catch(function (err) { delete productCache[handle]; throw err; });
    }
    return productCache[handle];
  }

  function postChange(line, item, props) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: item.quantity, properties: props })
    }).then(function (r) { if (!r.ok) throw new Error('change ' + r.status); return r.json(); });
  }

  var inflight = null;
  var lastLineCount = null;

  // Resolves with the number of lines that were updated. Never rejects.
  function sync() {
    if (inflight) return inflight;
    var changed = 0;
    inflight = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastLineCount = (cart.items || []).length;
        var handles = {};
        (cart.items || []).forEach(function (i) { if (i.handle) handles[i.handle] = true; });
        return Promise.all(Object.keys(handles).map(function (h) {
          return getProduct(h).then(function (p) { return [h, p]; }, function () { return [h, null]; });
        })).then(function (pairs) {
          var products = {};
          pairs.forEach(function (pr) { products[pr[0]] = pr[1]; });
          return step(cart, products, 0);
        });
      })
      .then(function () { return changed; }, function (err) { log('sync failed', err); return changed; })
      .then(function (n) { inflight = null; return n; });

    // One change at a time: a change can merge or reorder lines, so each
    // step works from the cart Shopify just returned.
    function step(cart, products, guard) {
      var items = cart.items || [];
      if (guard > items.length + 3) return Promise.resolve(cart);
      var cur = currencyFor();
      for (var idx = 0; idx < items.length; idx++) {
        var item = items[idx];
        var p = products[item.handle];
        var v = p && (p.variants || []).filter(function (x) { return x.id === item.variant_id; })[0];
        if (!v) continue;
        var want = fmt(msrpCents(v.price, v.compare_at_price));
        if (!want) continue;
        var props = item.properties || {};
        if (props._msrp === want && props._msrp_currency === cur) continue;
        var next = {};
        Object.keys(props).forEach(function (k) { next[k] = props[k]; });
        next._msrp = want;
        next._msrp_currency = cur;
        log('update line', idx + 1, props._msrp, '->', want, cur);
        changed++;
        return postChange(idx + 1, item, next).then(function (c) { return step(c, products, guard + 1); });
      }
      return Promise.resolve(cart);
    }
    return inflight;
  }

  // The bag must not look different afterwards: only hidden properties change.
  // If Shopify merged two lines, the drawer or cart page is out of step with the
  // cart, so reload the cart page. The drawer redraws on its next open.
  function afterSync(n) {
    if (!n) return n;
    var path = window.location.pathname.replace(/\/+$/, '');
    var cartUrl = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/+$/, '');
    if (path.slice(-cartUrl.length) === cartUrl) {
      return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        if ((c.items || []).length !== lastLineCount) window.location.reload();
        return n;
      }, function () { return n; });
    }
    return n;
  }

  // ── 3. Checkout guard ────────────────────────────────────────────────────
  // Listens on window in the capture phase, ahead of the sale guard in
  // native-anniversary-sale.js (which listens on document). After the refresh
  // the same click is replayed, so the sale guard still sees it and runs.
  var replaying = false;
  window.addEventListener('click', function (e) {
    if (replaying) return;
    var btn = e.target && e.target.closest ? e.target.closest('button[name="checkout"], input[name="checkout"]') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var done = false;
    var go = function () {
      if (done) return;
      done = true;
      replaying = true;
      try { btn.click(); } finally { replaying = false; }
    };
    var timer = setTimeout(go, 4000);                // never trap the shopper on a slow network
    sync().then(afterSync).then(function () { clearTimeout(timer); go(); }, function () { clearTimeout(timer); go(); });
  }, true);

  // ── Triggers ─────────────────────────────────────────────────────────────
  var debounce = null;
  function later() {
    clearTimeout(debounce);
    debounce = setTimeout(function () { sync().then(afterSync); }, 800);
  }

  function boot() {
    loadPdpUsPrices();
    var path = window.location.pathname.replace(/\/+$/, '');
    var cartUrl = ((window.routes && window.routes.cart_url) || '/cart').replace(/\/+$/, '');
    if (path.slice(-cartUrl.length) === cartUrl) later();
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, later);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  NS.msrp = { sync: sync, currencyFor: currencyFor, format: fmt, valueCents: msrpCents };
})();
